import { useCallback } from 'react'
import { useResource } from '@/components/admin/shared/hooks/use-resource'
import { getSourceDisplayNameFromFields } from '@/lib/source-display'
import type { RouterInputs, RouterOutputs } from '@/lib/trpc'
import { trpc } from '../../../../lib/trpc.js'
import type {
  Source,
  SourceCategory,
  SourceContent,
  SourceContentItem,
  SourceContentItemType,
  SourceContentSummary,
  SourceFormData,
} from '../types'

// `category` comes back as the DB column's plain `text` type. Narrow that
// one honestly-wider field instead of casting the whole tRPC payload into
// Source[]. (`list` already substitutes the company-derived display name,
// so `name` arrives non-null here.)
const selectSources = (
  data: RouterOutputs['admin']['sources']['list'] | undefined,
): Source[] =>
  (data ?? []).map((s) => ({
    ...s,
    category: s.category as SourceCategory,
  }))

export function useSources() {
  const utils = trpc.useUtils()

  const invalidate = useCallback(() => {
    utils.admin.sources.list.invalidate()
    utils.admin.sources.getById.invalidate()
  }, [utils])

  const {
    items: sources,
    loading,
    error,
    refresh,
    create,
    update,
    remove,
  } = useResource({
    query: trpc.admin.sources.list.useQuery(),
    select: selectSources,
    invalidate,
    create: trpc.admin.sources.create,
    update: trpc.admin.sources.update,
    remove: trpc.admin.sources.delete,
  })

  const addSource = async (formData: SourceFormData): Promise<Source> => {
    const s = await create(formData)
    return {
      ...s,
      category: s.category as SourceCategory,
      // `create` never links a company, so the stored name is the required
      // non-empty input; the coalesce only satisfies the nullable column type.
      name: s.name ?? '',
      companyId: null,
    }
  }

  const updateSource = async (
    id: string,
    formData: Partial<SourceFormData>,
  ): Promise<Source> => {
    const s = await update({ id, ...formData })
    return {
      ...s,
      category: s.category as SourceCategory,
      // `update` returns the raw stored column with no company join — null for
      // a company-backed source. Callers that care re-apply the display name.
      name: s.name ?? '',
      companyId: null,
    }
  }

  const deleteSource = async (id: string): Promise<void> => {
    await remove({ id })
  }

  const getSource = useCallback(
    async (id: string): Promise<Source> => {
      const s = await utils.admin.sources.getById.fetch({ id })
      return {
        ...s,
        category: s.category as SourceCategory,
        // getById hands back the raw column (null for company-backed sources)
        // plus the joined company name; Source.name is the display value.
        name: getSourceDisplayNameFromFields(s),
      }
    },
    [utils],
  )

  return {
    sources,
    loading,
    error,
    refresh,
    addSource,
    updateSource,
    deleteSource,
    getSource,
  }
}

const selectSourceContent = (
  data: RouterOutputs['admin']['content']['listSourceContent'] | undefined,
): SourceContent[] => data?.data ?? []

export function useSourceContent(sourceId: string | null) {
  const utils = trpc.useUtils()

  // Every write to a container can change its parent's summary and the set of
  // kinds offered as suggestions, so all three invalidate the same trio.
  const invalidate = useCallback(() => {
    utils.admin.content.listSourceContent.invalidate()
    utils.admin.content.getSourceContent.invalidate()
    utils.admin.content.listSourceContentKinds.invalidate()
  }, [utils])

  const {
    items: contents,
    data,
    loading,
    error,
    create,
    update,
    remove,
  } = useResource({
    query: trpc.admin.content.listSourceContent.useQuery(
      { sourceId: sourceId! },
      { enabled: !!sourceId },
    ),
    select: selectSourceContent,
    invalidate,
    create: trpc.admin.content.createSourceContent,
    update: trpc.admin.content.updateSourceContent,
    remove: trpc.admin.content.deleteSourceContent,
  })

  const summary: SourceContentSummary | null = data?.summary ?? null

  const refresh = useCallback(() => {
    utils.admin.content.listSourceContent.invalidate()
  }, [utils])

  const addContent = async (contentData: {
    sourceId: string
    title: string
    kind: string
    actionVerb?: string | null
    description?: string | null
    order?: number
    url?: string | null
    parentId?: string | null
    estimatedMinutes?: number | null
    icon?: string | null
  }) => {
    return await create(contentData)
  }

  // Callers only await this for its side effect (the list/detail queries are
  // invalidated separately by the mutation's onSuccess), and the router's
  // return type is narrower than the local SourceContent interface, so
  // return the router's own output type rather than forcing it into
  // SourceContent.
  const updateContent = async (
    id: string,
    contentData: Partial<{
      title: string
      kind: string
      actionVerb: string | null
      description: string | null
      order: number
      url: string | null
      parentId: string | null
      estimatedMinutes: number | null
      icon: string | null
    }>,
  ): Promise<RouterOutputs['admin']['content']['updateSourceContent']> => {
    return await update({ id, ...contentData })
  }

  const deleteContent = async (id: string): Promise<void> => {
    await remove({ id })
  }

  return {
    contents,
    summary,
    loading,
    error,
    refresh,
    addContent,
    updateContent,
    deleteContent,
  }
}

// The router's UnifiedContentItem types `contentType` as plain `string`
// (same looseness create/updateSourceContentItem have) — narrow that one
// field instead of casting the whole payload into SourceContentItem[].
const selectSourceContentItems = (
  data: RouterOutputs['admin']['content']['listSourceContentItems'] | undefined,
): SourceContentItem[] =>
  (data?.data ?? []).map((item) => ({
    ...item,
    contentType: item.contentType as SourceContentItemType,
  }))

export function useSourceContentItems(sourceContentId: string | null) {
  const utils = trpc.useUtils()

  const invalidate = useCallback(() => {
    utils.admin.content.listSourceContentItems.invalidate()
    utils.admin.content.listSourceContent.invalidate()
    utils.admin.content.getSourceContent.invalidate()
  }, [utils])

  const { items, loading, error, create, update, remove } = useResource({
    query: trpc.admin.content.listSourceContentItems.useQuery(
      { sourceContentId: sourceContentId! },
      { enabled: !!sourceContentId },
    ),
    select: selectSourceContentItems,
    invalidate,
    create: trpc.admin.content.createSourceContentItem,
    update: trpc.admin.content.updateSourceContentItem,
    remove: trpc.admin.content.deleteSourceContentItem,
  })

  const refresh = useCallback(() => {
    utils.admin.content.listSourceContentItems.invalidate()
  }, [utils])

  // create/updateSourceContentItem return `contentType` as a plain `string`
  // (the router's own narrowing — same looseness listSourceContentItems has),
  // not the `SourceContentItemType` literal union: genuinely narrower than
  // SourceContentItem. Callers only await these for their side effect.
  const addItem = async (
    itemData: RouterInputs['admin']['content']['createSourceContentItem'],
  ): Promise<RouterOutputs['admin']['content']['createSourceContentItem']> => {
    return await create(itemData)
  }

  const updateItem = async (
    id: string,
    itemData: Partial<{
      title: string | null
      url: string | null
      description: string | null
      order: number
      durationMinutes: number | null
      icon: string | null
    }>,
  ): Promise<RouterOutputs['admin']['content']['updateSourceContentItem']> => {
    // Filter out null title since tRPC schema expects string | undefined
    const { title, ...rest } = itemData
    return await update({
      id,
      ...rest,
      ...(title !== undefined && title !== null ? { title } : {}),
    })
  }

  const deleteItem = async (id: string): Promise<void> => {
    await remove({ id })
  }

  return {
    items,
    loading,
    error,
    refresh,
    addItem,
    updateItem,
    deleteItem,
  }
}

export function useSourceContentKinds() {
  const { data, isLoading: loading } =
    trpc.admin.content.listSourceContentKinds.useQuery()
  return { kinds: data?.kinds ?? [], loading }
}

export function useReorderChildren() {
  const utils = trpc.useUtils()

  const invalidateChildViews = useCallback(() => {
    utils.admin.content.listSourceContent.invalidate()
    utils.admin.content.listSourceContentItems.invalidate()
    utils.admin.content.getSourceContent.invalidate()
  }, [utils])

  const mutation = trpc.admin.content.reorderSourceContentChildren.useMutation({
    onSuccess: invalidateChildViews,
  })

  const reorderChildren = useCallback(
    async (input: {
      sourceId: string
      parentId: string | null
      children: Array<{ type: 'container' | 'item'; id: string }>
    }): Promise<void> => {
      await mutation.mutateAsync(input)
    },
    [mutation],
  )

  return {
    reorderChildren,
    invalidateChildViews,
    isReordering: mutation.isPending,
  }
}
